import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const S3_BUCKET_URL =
  "https://public-bucket-prd-ee73a5e.s3.amazonaws.com/locales";
const SUPPORTED_LANGS = ["es", "en"] as const;
const localTranslationModules = import.meta.glob(
  "../locales/**/*.json",
  { import: "default", eager: true }
) as Record<string, Record<string, string>>;

const normalizeLanguage = (language?: string | null) => {
  if (!language) return "es";
  return language.startsWith("en") ? "en" : "es";
};

const detectInitialLanguage = () => {
  const urlLang = new URLSearchParams(window.location.search).get("lang");
  if (urlLang) {
    const normalized = normalizeLanguage(urlLang);
    localStorage.setItem("language", normalized);
    return normalized;
  }

  const storedLanguage =
    localStorage.getItem("language") || localStorage.getItem("i18nextLng");

  if (storedLanguage) {
    const normalized = normalizeLanguage(storedLanguage);
    localStorage.setItem("language", normalized);
    localStorage.removeItem("i18nextLng");
    return normalized;
  }

  const browserLanguage = normalizeLanguage(
    navigator.languages?.[0] || navigator.language
  );
  return browserLanguage;
};

// Helper function to load translations from S3
export const loadNamespaceTranslations = async (
  namespace: string,
  language: string
) => {
  try {
    if (!i18n.hasResourceBundle(language, namespace)) {
      const localKey = `../locales/${language}/${namespace}.json`;
      const localTranslations = localTranslationModules[localKey];

      if (localTranslations) {
        i18n.addResourceBundle(
          language,
          namespace,
          localTranslations,
          true,
          true
        );
        return;
      }

      const response = await fetch(
        `${S3_BUCKET_URL}/${namespace}/${language}.json`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const translations = await response.json();
      i18n.addResourceBundle(language, namespace, translations, true, true);
    }
  } catch (error) {
    console.error(
      `Failed to load translations for ${namespace}/${language}:`,
      error
    );
    if (language !== "es") {
      await loadNamespaceTranslations(namespace, "es");
    }
  }
};

// Function to preload translations for a specific page
export const preloadPageTranslations = async (namespace: string) => {
  const languages = ["es", "en"]; // Spanish first, then English
  await Promise.all(
    languages.map((lang) => loadNamespaceTranslations(namespace, lang))
  );
};

// Initialize i18n
const initializeI18n = async () => {
  const initialLanguage = detectInitialLanguage();

  await i18n.use(initReactI18next).init({
    lng: initialLanguage,
    supportedLngs: SUPPORTED_LANGS,
    fallbackLng: ["es", "en"],
    interpolation: {
      escapeValue: false,
    },
    resources: {
      es: {},
      en: {},
    },
  });

  // Load initial translations for the home page using the detected language
  await loadNamespaceTranslations("home", initialLanguage);

  return i18n;
};

export const i18nInstance = initializeI18n();
export default i18n;
